import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { nombreSeguroStorage } from '../../lib/nombre-storage';
import { requiresCd513Investigation } from '../../lib/cd513-catalog';
import { syncIncidentCompliance } from '../../lib/incident-compliance-sync';
import { ORIGENES_PRESUNCION } from '../../lib/incidentes/inc-datos';
import { validateFileSize } from '../../lib/validateFileSize';
import { openIncidentEvidence } from '../../lib/incidentEvidence';
import SelectBuscable from '../SelectBuscable';

// ── Types ──────────────────────────────────────────────────────────────────────

export type IncidentType = 'accidente_trabajo' | 'incidente' | 'enfermedad_profesional' | 'accidente_transito' | 'accidente_acto_servicio';
export type Severity = 'leve' | 'grave' | 'muy_grave' | 'mortal';
export type IncidentStatus = 'abierto' | 'en_investigacion' | 'cerrado';

export interface Incident {
  id: string;
  company_id: string;
  incident_type: IncidentType;
  incident_date: string;
  incident_time: string | null;
  severity: Severity;
  warehouse_id: string | null;
  worker_id: string | null;
  description: string;
  body_part_affected: string | null;
  injury_type: string | null;
  days_lost: number;
  medical_leave: boolean;
  iess_reported: boolean;
  iess_report_date: string | null;
  iess_report_number: string | null;
  status: IncidentStatus;
  investigator_id: string | null;
  investigation_date: string | null;
  immediate_cause: string | null;
  basic_cause: string | null;
  corrective_actions: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  position_id: string | null;
  witnesses: { nombre: string; cargo: string }[] | null;
  created_at: string;
  // M46 (lote E): presunción de enfermedad profesional y constancia del aviso al IESS.
  disease_risk_agent?: string | null;
  disease_physician?: string | null;
  disease_origin?: string | null;
  disease_origin_detail?: string | null;
  iess_constancia_path?: string | null;
  iess_constancia_name?: string | null;
  iess_constancia_mime?: string | null;
}

interface WorkerOption { id: string; full_name: string; cedula: string | null; }
interface WarehouseOption { id: string; name: string; }
interface ProfileOption { id: string; full_name: string | null; email: string | null; }

interface Props {
  companyId: string;
  incident?: Incident | null;
  tipoInicial?: IncidentType;   // lote E: "+ Presunción de enfermedad profesional" abre el formulario con ese tipo
  workers: WorkerOption[];
  warehouses: WarehouseOption[];
  onSaved: () => void;
  onClose: () => void;
}

// ── Labels ─────────────────────────────────────────────────────────────────────

export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  accidente_trabajo:       'Accidente de trabajo',
  incidente:               'Incidente (casi-accidente)',
  enfermedad_profesional:  'Presunción de enfermedad profesional',
  accidente_transito:      'Accidente de tránsito',
  accidente_acto_servicio: 'Accidente en acto de servicio',
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  leve:      'Leve',
  grave:     'Grave',
  muy_grave: 'Muy grave',
  mortal:    'Mortal',
};

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  abierto:          'Abierto',
  en_investigacion: 'En investigación',
  cerrado:          'Cerrado',
};

export const SEVERITY_COLORS: Record<Severity, { bg: string; color: string; border: string }> = {
  leve:      { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
  grave:     { bg: 'rgba(245,158,11,0.1)',   color: '#f59e0b', border: 'rgba(245,158,11,0.3)'  },
  muy_grave: { bg: 'rgba(249,115,22,0.1)',   color: '#fb923c', border: 'rgba(249,115,22,0.3)'  },
  mortal:    { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444', border: 'rgba(239,68,68,0.3)'   },
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function IncidentModal({ companyId, incident, tipoInicial, workers, warehouses, onSaved, onClose }: Props) {
  const isEdit = !!incident;

  // Datos del evento
  const [incidentType, setIncidentType]   = useState<IncidentType>(incident?.incident_type ?? tipoInicial ?? 'accidente_trabajo');
  const [incidentDate, setIncidentDate]   = useState(incident?.incident_date ?? '');
  const [incidentTime, setIncidentTime]   = useState(incident?.incident_time ?? '');
  const [severity, setSeverity]           = useState<Severity>(incident?.severity ?? 'leve');
  const [warehouseId, setWarehouseId]     = useState(incident?.warehouse_id ?? '');
  const [workerId, setWorkerId]           = useState(incident?.worker_id ?? '');
  const [workerSearch, setWorkerSearch]   = useState('');
  const [showWorkerDrop, setShowWorkerDrop] = useState(false);
  const workerRef = useRef<HTMLDivElement>(null);
  const [positionId, setPositionId] = useState(incident?.position_id ?? '');
  const [jobPositions, setJobPositions] = useState<{ id: string; name: string }[]>([]);
  const [witnesses, setWitnesses] = useState<{ nombre: string; cargo: string }[]>(incident?.witnesses ?? []);

  // Descripción
  const [description, setDescription]         = useState(incident?.description ?? '');
  const [bodyPart, setBodyPart]               = useState(incident?.body_part_affected ?? '');
  const [injuryType, setInjuryType]           = useState(incident?.injury_type ?? '');
  const [daysLost, setDaysLost]               = useState(String(incident?.days_lost ?? '0'));
  const [medicalLeave, setMedicalLeave]       = useState(incident?.medical_leave ?? false);

  // Presunción de enfermedad profesional (lote E). El aviso al IESS y las medidas correctivas se registran en
  // "Aviso y medidas" (IncidentAvisoMedidasModal), no aquí: este formulario ya no los toca.
  const [diseaseAgent, setDiseaseAgent]       = useState(incident?.disease_risk_agent ?? '');
  const [diseasePhysician, setDiseasePhysician] = useState(incident?.disease_physician ?? '');
  const [diseaseOrigin, setDiseaseOrigin]     = useState(incident?.disease_origin ?? '');
  const [diseaseOriginDetail, setDiseaseOriginDetail] = useState(incident?.disease_origin_detail ?? '');
  const esPresuncion = incidentType === 'enfermedad_profesional';

  // Investigación
  const [status, setStatus]                   = useState<IncidentStatus>(incident?.status ?? 'abierto');
  const [investigatorId, setInvestigatorId]   = useState(incident?.investigator_id ?? '');
  const [investigationDate, setInvestigationDate] = useState(incident?.investigation_date ?? '');
  const [immediateCause, setImmediateCause]   = useState(incident?.immediate_cause ?? '');
  const [basicCause, setBasicCause]           = useState(incident?.basic_cause ?? '');
  const [correctiveActions, setCorrectiveActions] = useState(incident?.corrective_actions ?? '');

  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [saving, setSaving]     = useState(false);
  const [errors, setErrors]     = useState<Record<string, string>>({});

  // Archivo adjunto (informe / evidencia)
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Init worker search label from existing incident
  useEffect(() => {
    if (incident?.worker_id) {
      const w = workers.find(w => w.id === incident.worker_id);
      if (w) setWorkerSearch(`${w.full_name}${w.cedula ? ` (${w.cedula})` : ''}`);
    }
  }, [incident?.worker_id, workers]);

  // Load profiles
  useEffect(() => {
    supabase.from('profiles')
      .select('id, full_name, email')
      .eq('company_id', companyId)
      .eq('active', true)
      .order('full_name')
      .then(({ data }) => setProfiles((data ?? []) as ProfileOption[]));
  }, [companyId]);

  // Load job positions
  useEffect(() => {
    (async () => {
      const { data: jps } = await supabase
        .from('job_positions')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');
      setJobPositions((jps ?? []) as { id: string; name: string }[]);
    })();
  }, [companyId]);

  // Click outside worker dropdown
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (workerRef.current && !workerRef.current.contains(e.target as Node)) setShowWorkerDrop(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredWorkers = workers.filter(w => {
    if (!workerSearch) return true;
    const q = workerSearch.toLowerCase();
    return w.full_name.toLowerCase().includes(q) || (w.cedula ?? '').includes(q);
  }).slice(0, 8);

  function selectWorker(w: WorkerOption) {
    setWorkerId(w.id);
    setWorkerSearch(`${w.full_name}${w.cedula ? ` (${w.cedula})` : ''}`);
    setShowWorkerDrop(false);
  }

  function clearWorker() {
    setWorkerId('');
    setWorkerSearch('');
  }

  function addWitness() {
    setWitnesses(prev => [...prev, { nombre: '', cargo: '' }]);
  }

  function updateWitness(idx: number, field: 'nombre' | 'cargo', value: string) {
    setWitnesses(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  }

  function removeWitness(idx: number) {
    setWitnesses(prev => prev.filter((_, i) => i !== idx));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!incidentDate) e.date = 'La fecha del evento es requerida.';
    if (!description.trim()) e.description = 'La descripción es requerida.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    if (attachmentFile) {
      const sizeError = validateFileSize(attachmentFile, 20);
      if (sizeError) { setErrors(prev => ({ ...prev, attachment: sizeError })); return; }
    }
    setSaving(true);

    const payload = {
      company_id:          companyId,
      incident_type:       incidentType,
      incident_date:       incidentDate,
      incident_time:       incidentTime || null,
      severity,
      warehouse_id:        warehouseId || null,
      worker_id:           workerId || null,
      position_id:         positionId || null,
      witnesses:           witnesses.length > 0 ? witnesses : [],
      description:         description.trim(),
      body_part_affected:  bodyPart.trim() || null,
      injury_type:         injuryType.trim() || null,
      days_lost:           parseInt(daysLost) || 0,
      medical_leave:       medicalLeave,
      disease_risk_agent:    esPresuncion ? diseaseAgent.trim() || null : null,
      disease_physician:     esPresuncion ? diseasePhysician.trim() || null : null,
      disease_origin:        esPresuncion ? diseaseOrigin || null : null,
      disease_origin_detail: esPresuncion ? diseaseOriginDetail.trim() || null : null,
      status,
      investigator_id:     investigatorId || null,
      investigation_date:  investigationDate || null,
      immediate_cause:     immediateCause.trim() || null,
      basic_cause:         basicCause.trim() || null,
      corrective_actions:  correctiveActions.trim() || null,
    };

    let incidentId = incident?.id;
    if (isEdit && incidentId) {
      const { error } = await supabase.from('incidents').update(payload).eq('id', incidentId);
      if (error) {
        alert('Error al guardar el incidente: ' + error.message);
        setSaving(false);
        return;
      }
    } else {
      const { data, error } = await supabase.from('incidents').insert(payload).select('id').single();
      if (error) {
        alert('Error al registrar el incidente: ' + error.message);
        setSaving(false);
        return;
      }
      incidentId = data?.id;
    }

    // Upload attachment to storage
    if (attachmentFile && incidentId) {
      const ext = attachmentFile.name.split('.').pop();
      const filePath = `${companyId}/${nombreSeguroStorage(`${incidentId}.${ext}`)}`;
      const { error: upError } = await supabase.storage
        .from('incident-evidence')
        .upload(filePath, attachmentFile, { upsert: true });

      if (upError) {
        console.error('Error al subir el adjunto a incident-evidence:', upError);
        setErrors(prev => ({ ...prev, attachment: 'El incidente se guardó, pero no se pudo subir el archivo. Intenta de nuevo.' }));
        setSaving(false);
        return;
      }

      const { error: urlUpdateError } = await supabase.from('incidents').update({
        attachment_url: filePath,
        attachment_name: attachmentFile.name,
      }).eq('id', incidentId);
      if (urlUpdateError) {
        console.error('Error al vincular el adjunto al incidente:', urlUpdateError);
        setErrors(prev => ({ ...prev, attachment: 'El archivo se subió, pero no se pudo vincular al incidente. Intenta de nuevo.' }));
        setSaving(false);
        return;
      }
    }

    syncIncidentCompliance(companyId).catch(() => {});

    setSaving(false);
    onSaved();
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 640, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
            {isEdit ? 'Editar registro' : tipoInicial === 'enfermedad_profesional' ? 'Registrar presunción de enfermedad profesional' : 'Registrar accidente / incidente'}
          </h2>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* ── 1. Datos del evento ──────────────────────────────────── */}
          <Sect title="Datos del evento">
            <Row>
              <Field label="Tipo de evento *">
                <select style={sel} value={incidentType} onChange={e => setIncidentType(e.target.value as IncidentType)}>
                  {(Object.keys(INCIDENT_TYPE_LABELS) as IncidentType[]).map(t =>
                    <option key={t} value={t}>{INCIDENT_TYPE_LABELS[t]}</option>
                  )}
                </select>
              </Field>
              <Field label="Gravedad">
                <select style={sel} value={severity} onChange={e => setSeverity(e.target.value as Severity)}>
                  {(Object.keys(SEVERITY_LABELS) as Severity[]).map(s =>
                    <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>
                  )}
                </select>
              </Field>
            </Row>
            {requiresCd513Investigation(severity, Number(daysLost)) && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, margin: '8px 0',
                padding: '10px 12px', borderRadius: 8,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
              }}>
                <span style={{ flexShrink: 0, fontSize: '0.9rem' }}>⚠️</span>
                <span style={{ color: '#f87171', fontSize: '0.78rem', lineHeight: 1.4 }}>
                  Este incidente requiere <strong>Investigación formal CD 513</strong>
                  (Art. Tercer Anexo, IESS). Hazla desde el enlace "Investigación CD 513"
                  en la lista de incidentes, después de guardar este registro.
                </span>
              </div>
            )}
            <Row>
              <Field label={esPresuncion ? 'Fecha en que se advirtieron los indicios *' : 'Fecha del evento *'} error={errors.date}>
                <input style={inp} type="date" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} />
              </Field>
              <Field label="Hora del evento">
                <input style={inp} type="time" value={incidentTime} onChange={e => setIncidentTime(e.target.value)} />
              </Field>
            </Row>
            <Field label="Bodega / Centro de trabajo">
              <SelectBuscable inputStyle={selBuscable} ariaLabel="Bodega / Centro de trabajo" value={warehouseId} onChange={setWarehouseId}
                vacio="— Sin especificar —" opciones={warehouses.map(w => ({ value: w.id, label: w.name }))} />
            </Field>
            <Field label="Trabajador afectado">
              <div ref={workerRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="Buscar por nombre o cédula…"
                    value={workerSearch}
                    onChange={e => { setWorkerSearch(e.target.value); setWorkerId(''); setShowWorkerDrop(true); }}
                    onFocus={() => setShowWorkerDrop(true)}
                  />
                  {workerId && (
                    <button onClick={clearWorker} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0 8px', fontSize: '1rem' }}>✕</button>
                  )}
                </div>
                {showWorkerDrop && filteredWorkers.length > 0 && (
                  <div style={dropStyle}>
                    {filteredWorkers.map(w => (
                      <button key={w.id} style={dropItem} onClick={() => selectWorker(w)}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{w.full_name}</span>
                        {w.cedula && <span style={{ color: '#64748b', fontSize: '0.78rem', marginLeft: 8 }}>{w.cedula}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>
            <Field label="Puesto de trabajo afectado (opcional)">
              <SelectBuscable
                inputStyle={{ ...selBuscable, opacity: jobPositions.length === 0 ? 0.5 : 1 }}
                ariaLabel="Puesto de trabajo afectado (opcional)"
                value={positionId}
                onChange={setPositionId}
                disabled={jobPositions.length === 0}
                vacio="— Sin puesto específico —"
                opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))}
              />
            </Field>
          </Sect>

          {esPresuncion && (
            <Sect title="Presunción de enfermedad profesional">
              <Row>
                <Field label="Agente o factor de riesgo">
                  <input style={inp} value={diseaseAgent} onChange={e => setDiseaseAgent(e.target.value)} placeholder="Ej: Ruido, sílice, posturas forzadas…" />
                </Field>
                <Field label="Médico que advirtió los indicios">
                  <input style={inp} value={diseasePhysician} onChange={e => setDiseasePhysician(e.target.value)} placeholder="Nombre del médico" />
                </Field>
              </Row>
              <Row>
                <Field label="Origen">
                  <select style={sel} value={diseaseOrigin} onChange={e => setDiseaseOrigin(e.target.value)}>
                    <option value="">— Sin especificar —</option>
                    {ORIGENES_PRESUNCION.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
                  </select>
                </Field>
                <Field label="Detalle del origen">
                  <input style={inp} value={diseaseOriginDetail} onChange={e => setDiseaseOriginDetail(e.target.value)} placeholder="Ej: examen periódico del 10/10" />
                </Field>
              </Row>
            </Sect>
          )}

          {/* ── 2. Descripción del evento ─────────────────────────────── */}
          <Sect title="Descripción del evento">
            <Field label="Descripción *" error={errors.description}>
              <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe qué ocurrió, cómo y dónde…" />
            </Field>
            <Row>
              <Field label="Parte del cuerpo afectada">
                <input style={inp} value={bodyPart} onChange={e => setBodyPart(e.target.value)} placeholder="Ej: Mano derecha" />
              </Field>
              <Field label="Tipo de lesión">
                <input style={inp} value={injuryType} onChange={e => setInjuryType(e.target.value)} placeholder="Ej: Corte, quemadura…" />
              </Field>
            </Row>
            <Row>
              <Field label="Días perdidos">
                <input style={inp} type="number" min={0} value={daysLost} onChange={e => setDaysLost(e.target.value)} />
              </Field>
              <Field label=" ">
                <Toggle label="Requirió licencia médica" value={medicalLeave} onChange={setMedicalLeave} />
              </Field>
            </Row>
            <Field label="Testigos">
              {witnesses.length === 0 && (
                <p style={{ color: '#475569', fontSize: '0.82rem', margin: '0 0 4px' }}>Sin testigos registrados.</p>
              )}
              {witnesses.map((w, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="Nombre"
                    value={w.nombre}
                    onChange={e => updateWitness(idx, 'nombre', e.target.value)}
                  />
                  <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="Cargo"
                    value={w.cargo}
                    onChange={e => updateWitness(idx, 'cargo', e.target.value)}
                  />
                  <button onClick={() => removeWitness(idx)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0 4px', fontSize: '0.9rem', flexShrink: 0 }}>✕</button>
                </div>
              ))}
              <button onClick={addWitness} style={{
                width: '100%', padding: '9px', background: 'transparent',
                border: '1px dashed #334155', borderRadius: 8, color: '#64748b',
                cursor: 'pointer', fontSize: '0.84rem', transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#64748b'; }}>
                + Agregar testigo
              </button>
            </Field>
          </Sect>

          {/* ── 3. Aviso al IESS (lote E: se registra en "Aviso y medidas") ── */}
          {(isEdit && incidentType !== 'incidente') && (
            <Sect title="Aviso al IESS y medidas correctivas">
              <div style={{ padding: '10px 14px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, fontSize: '0.79rem', color: '#93c5fd', lineHeight: 1.45 }}>
                El aviso al IESS (número, fecha de envío y constancia) y las medidas correctivas con su evidencia se registran en
                "Aviso y medidas", en la lista de casos. El término es de 10 días hábiles (Resolución C.D. 513 del IESS).
              </div>
            </Sect>
          )}

          {/* ── 4. Investigación y cierre ─────────────────────────────── */}
          <Sect title="Investigación y cierre">
            <Row>
              <Field label="Estado">
                <select style={sel} value={status} onChange={e => setStatus(e.target.value as IncidentStatus)}>
                  {(Object.keys(INCIDENT_STATUS_LABELS) as IncidentStatus[]).map(s =>
                    <option key={s} value={s}>{INCIDENT_STATUS_LABELS[s]}</option>
                  )}
                </select>
              </Field>
              <Field label="Investigador responsable">
                <select style={sel} value={investigatorId} onChange={e => setInvestigatorId(e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name || p.email}</option>)}
                </select>
              </Field>
            </Row>
            <Field label="Fecha de investigación">
              <input style={inp} type="date" value={investigationDate} onChange={e => setInvestigationDate(e.target.value)} />
            </Field>
            <Field label="Causa inmediata">
              <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={immediateCause} onChange={e => setImmediateCause(e.target.value)} placeholder="Actos y condiciones inseguras directas…" />
            </Field>
            <Field label="Causa básica / raíz">
              <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={basicCause} onChange={e => setBasicCause(e.target.value)} placeholder="Factores personales y de trabajo subyacentes…" />
            </Field>
            <Field label="Acciones correctivas generales">
              <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={correctiveActions} onChange={e => setCorrectiveActions(e.target.value)} placeholder="Resumen de medidas correctivas a implementar…" />
            </Field>
            <Field label="Informe de investigación o evidencia (PDF/imagen)" error={errors.attachment}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incident?.attachment_url && !attachmentFile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => openIncidentEvidence(incident)}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#38bdf8', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Ver archivo adjunto
                    </button>
                    <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{incident.attachment_name}</span>
                  </div>
                )}
                {attachmentFile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>{attachmentFile.name}</span>
                    <button
                      type="button"
                      onClick={() => { setAttachmentFile(null); if (attachmentInputRef.current) attachmentInputRef.current.value = ''; }}
                      style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.78rem' }}
                    >
                      Quitar
                    </button>
                  </div>
                )}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#94a3b8', fontSize: '0.82rem', cursor: 'pointer', width: 'fit-content' }}>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setAttachmentFile(e.target.files?.[0] ?? null)}
                    style={{ display: 'none' }}
                  />
                  {(attachmentFile || incident?.attachment_url) ? 'Reemplazar archivo' : 'Subir archivo'}
                </label>
              </div>
            </Field>
          </Sect>

        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={cancelBtn}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Registrar evento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Sect({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 20 }}>
      <p style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 12, textTransform: 'uppercase' }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
      {error && <span style={{ color: '#f87171', fontSize: '0.74rem' }}>{error}</span>}
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', paddingTop: 4 }} onClick={() => onChange(!value)}>
      <div style={{ width: 38, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative', background: value ? 'rgba(47,191,113,0.25)' : '#334155', border: `2px solid ${value ? '#2FBF71' : '#475569'}`, transition: 'all 0.2s' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', position: 'absolute', top: 2, left: value ? 20 : 2, transition: 'left 0.2s', background: value ? '#2FBF71' : '#64748b' }} />
      </div>
      <span style={{ color: '#cbd5e1', fontSize: '0.84rem' }}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel: React.CSSProperties = { ...inp, cursor: 'pointer' };
// PE-C1: bodega y puesto con buscador (mismo aspecto; espacio a la derecha para la flecha).
const selBuscable: React.CSSProperties = { ...inp, paddingRight: 32 };
const dropStyle: React.CSSProperties = { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#1e293b', border: '1px solid #334155', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' };
const dropItem: React.CSSProperties = { display: 'flex', alignItems: 'center', width: '100%', padding: '9px 14px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn: React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn: React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
